import React, { useContext } from 'react';
import { Box, Button, Card, Divider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { UIContext } from '../../context/UIContext';
import { LocationV1 } from '../../types';

const InfoBox = styled(Card, { shouldForwardProp: (prop) => prop !== 'colorMode' })<{
  colorMode?: string;
}>(({ theme, colorMode }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: ' 16px 16px 0',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '16px 24px 0',
  },
  border: `1px ${
    colorMode === 'light' ? theme.palette.grey['12p'] : theme.palette.grey['23p']
  } solid`,
  ':hover': {
    cursor: 'pointer',
    backgroundColor: `1px ${theme.palette.primary.main} solid`,
  },
}));

const InfoAddressCard = ({
  address,
  countries,
  clickEditButton,
  clickDeleteButton,
}: {
  address: LocationV1;
  countries: { value: string; label: string }[];
  clickEditButton: () => void;
  clickDeleteButton: (id: number) => void;
}) => {
  const { t } = useTranslation();
  const { colorMode } = useContext(UIContext);
  const countryName =
    countries.find((obj) => obj.value === address.countryName)?.label || address.countryName || '';

  return (
    <InfoBox
      elevation={0}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
        minHeight: '280px',
        flex: 1,
        cursor: 'default !important',
      }}
      colorMode={colorMode}
    >
      <Typography marginBottom={0.5} variant="h3">
        {address.name}
      </Typography>
      {address.description && <Typography>{address.description}</Typography>}
      {address.address1 && <Typography>{address.address1}</Typography>}
      {address.address2 && <Typography>{address.address2}</Typography>}
      <Typography>
        {[address.town, address.county, address.postCode].filter(Boolean).join(', ')}
      </Typography>
      <Typography>{countryName}</Typography>
      {address.emailAddress && <Typography>{address.emailAddress}</Typography>}
      {address.phoneNumber && <Typography>{address.phoneNumber}</Typography>}
      <Box
        sx={{
          width: '100%',
          marginTop: 'auto',
        }}
      >
        <Divider
          sx={{
            margin: {
              xs: '22px -16px 0',
              sm: '22px -24px 0',
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginRight: '-16px',
            padding: '8px 0',
          }}
        >
          {!address?.isDefault && (
            <Button
              type="button"
              sx={{
                color: 'error.main',
                ':hover': {
                  backgroundColor: colorMode === 'light' ? 'grey.4p' : 'grey.18p',
                },
              }}
              startIcon={<DeleteOutlineIcon />}
              onClick={() => clickDeleteButton(address.id)}
            >
              {t('actions.delete')}
            </Button>
          )}
          <Button
            type="button"
            sx={{
              color: 'text.secondary',
              ':hover': {
                backgroundColor: colorMode === 'light' ? 'grey.4p' : 'grey.18p',
              },
            }}
            startIcon={<ModeEditOutlinedIcon />}
            onClick={clickEditButton}
          >
            {t('actions.edit')}
          </Button>
        </Box>
      </Box>
    </InfoBox>
  );
};

export default InfoAddressCard;
